<template>
  <div class="container py-5 overflow-y-auto">
    <div class="row justify-content-between align-items-center mb-4">
      <div class="col">
        <h1>ESO Integration</h1>
      </div>
      <div class="col-auto">
        <button class="btn btn-primary" @click="syncMeterMetadataReq">
          <i class="fas fa-sync me-2"></i>Sync ESO Data
        </button>
      </div>
    </div>

    <!-- Status Cards -->
    <div class="row g-3 mb-4">
      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Companies with Permissions</h5>
            <h2 class="mb-0">{{ companies.length }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Total Objects</h5>
            <h2 class="mb-0">{{ totalObjects }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Total Generation</h5>
            <h2 class="mb-0">{{ totalGeneration.toFixed(2) }} MWh</h2>
          </div>
        </div>
      </div>
    </div>

    <!-- Companies Section -->
    <div class="card mb-4">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h2 class="h5 mb-0">Company Permissions</h2>
        <div class="d-flex gap-2">
          <ActionButtonWithSpinner
            variant="btn-primary me-2"
            size="md"
            :busy="batchRequestInProgress"
            :disabled="selectedCompanies.length === 0"
            @click="requestPermissionsForSelected"
          >
            Request Permissions for Selected ({{ selectedCompanies.length }})
          </ActionButtonWithSpinner>
          <div class="input-group" style="width: 300px">
            <input
              type="text"
              class="form-control"
              v-model="companySearch"
              placeholder="Search companies..."
            />
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="isAllSelected"
                    @change="toggleSelectAll"
                    :disabled="filteredCompanies.length === 0"
                  />
                </div>
              </th>
              <th>Company Name</th>
              <th>Registry Code</th>
              <th>Actions</th>
              <th>Object Numbers</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="company in filteredCompanies"
              :key="company.registryCode"
              :class="{ 'table-danger': companyErrors[company.registryCode] }"
            >
              <td>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="
                      selectedCompanyCodes.includes(company.registryCode)
                    "
                    @change="toggleSelectCompany(company.registryCode)"
                  />
                </div>
              </td>
              <td>{{ company.companyName }}</td>
              <td>{{ company.registryCode }}</td>
              <td>
                <ActionButtonWithSpinner
                  variant="btn-primary"
                  :busy="loadingPermissions[company.registryCode]"
                  @click="askPermissionsRequest(company.registryCode)"
                >
                  Request Permissions
                </ActionButtonWithSpinner>
              </td>
              <td>
                <span
                  v-for="num in company.objectNumbers"
                  :key="num"
                  class="badge bg-secondary me-1 mb-1"
                >
                  {{ num }}
                </span>
              </td>
              <td>
                <div
                  v-if="companyErrors[company.registryCode]"
                  class="text-danger"
                >
                  <i class="fas fa-exclamation-circle me-1"></i>
                  Error: {{ companyErrors[company.registryCode] }}
                </div>
              </td>
            </tr>
            <tr v-if="filteredCompanies.length === 0">
              <td colspan="6" class="text-center py-4">No companies found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Generation Data Section -->
    <div class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h2 class="h5 mb-0">Generation Data</h2>
        <div class="d-flex gap-2">
          <button class="btn btn-primary" @click="askGenerationDataRequest">
            <i class="fas fa-download me-2"></i>Query Generation Data
          </button>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Object Address</th>
              <th>Object Number</th>
              <th>Amount (MWh)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="data in generationData" :key="data.objectNumber">
              <td>{{ data.companyName }}</td>
              <td>{{ data.objectAddress }}</td>
              <td>{{ data.objectNumber }}</td>
              <td>{{ data.amount.toFixed(2) }}</td>
            </tr>
            <tr v-if="generationData.length === 0">
              <td colspan="4" class="text-center py-4">
                No generation data available
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import { apiErrorMessage } from "@/utils/apiErrors";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  useAsyncTask,
  type AsyncTaskStartResponse,
} from "@/composables/useAsyncTask";
import { computed, ref, watch } from "vue";

interface EsoCompany {
  companyName: string;
  registryCode: string;
  objectNumbers: string[];
}

interface GenerationData {
  companyName: string;
  objectAddress: string;
  objectNumber: string;
  amount: number;
}

const { globalErrorHandler } = useGlobalHandlers();
const companies = ref<EsoCompany[]>([]);
const generationData = ref<GenerationData[]>([]);
const companySearch = ref("");
const loadingPermissions = ref<Record<string, boolean>>({});
const selectedCompanyCodes = ref<string[]>([]);
const batchRequestInProgress = ref(false);
const companyErrors = ref<Record<string, string>>({});

const totalObjects = computed(() => {
  return companies.value.reduce(
    (acc, company) => acc + company.objectNumbers.length,
    0
  );
});

const totalGeneration = computed(() => {
  return generationData.value.reduce((acc, data) => acc + data.amount, 0);
});

const filteredCompanies = computed(() => {
  if (!companySearch.value) return companies.value;
  const search = companySearch.value.toLowerCase();
  return companies.value.filter(
    (company) =>
      company.companyName.toLowerCase().includes(search) ||
      company.registryCode.toLowerCase().includes(search) ||
      company.objectNumbers.some((num) => num.toLowerCase().includes(search))
  );
});

const selectedCompanies = computed(() => {
  return companies.value.filter((company) =>
    selectedCompanyCodes.value.includes(company.registryCode)
  );
});

const isAllSelected = computed(() => {
  return (
    filteredCompanies.value.length > 0 &&
    filteredCompanies.value.every((company) =>
      selectedCompanyCodes.value.includes(company.registryCode)
    )
  );
});

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    // Deselect all filtered companies
    const filteredCodes = filteredCompanies.value.map((c) => c.registryCode);
    selectedCompanyCodes.value = selectedCompanyCodes.value.filter(
      (code) => !filteredCodes.includes(code)
    );
  } else {
    // Select all filtered companies
    const filteredCodes = filteredCompanies.value.map((c) => c.registryCode);
    const newSelection = [
      ...new Set([...selectedCompanyCodes.value, ...filteredCodes]),
    ];
    selectedCompanyCodes.value = newSelection;
  }
};

const toggleSelectCompany = (registryCode: string) => {
  if (selectedCompanyCodes.value.includes(registryCode)) {
    selectedCompanyCodes.value = selectedCompanyCodes.value.filter(
      (code) => code !== registryCode
    );
  } else {
    selectedCompanyCodes.value.push(registryCode);
  }
};

const makeApiRequest = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/lt-eso/list-companies");
    if (!response.data) {
      throw new Error("No data received from server");
    }
    companies.value = response.data;
  } catch (error: any) {
    globalErrorHandler(error.response?.data || error);
    companies.value = [];
  }
};

const askGenerationDataRequest = async () => {
  try {
    const response = await goSolidApi.get(
      "/api/admin/lt-eso/ask-generation-data"
    );
    if (!response.data) {
      throw new Error("No data received from server");
    }
    generationData.value = response.data;
  } catch (error: any) {
    globalErrorHandler(error.response?.data || error);
    generationData.value = [];
  }
};

const askPermissionsRequest = async (registryCode: string) => {
  try {
    loadingPermissions.value[registryCode] = true;
    companyErrors.value[registryCode] = ""; // Clear previous errors

    await goSolidApi.post(`/api/admin/lt-eso/ask-permissions/${registryCode}`);
    await makeApiRequest();
  } catch (error: any) {
    const errorMessage = apiErrorMessage(error, "Unknown error");
    companyErrors.value[registryCode] = errorMessage;
    globalErrorHandler(error.response?.data || error);
  } finally {
    loadingPermissions.value[registryCode] = false;
  }
};

const requestPermissionsForSelected = async () => {
  if (selectedCompanyCodes.value.length === 0 || batchRequestInProgress.value) {
    return;
  }

  batchRequestInProgress.value = true;
  companyErrors.value = {}; // Clear all previous errors

  for (const registryCode of selectedCompanyCodes.value) {
    await askPermissionsRequest(registryCode);
  }

  batchRequestInProgress.value = false;
};

const syncMetadataTask = useAsyncTask<{
  year: number;
  month: number;
  status: string;
}>();

watch(syncMetadataTask.state, async (state) => {
  if (state === "success") {
    try {
      await makeApiRequest();
      await askGenerationDataRequest();
    } catch (error: any) {
      globalErrorHandler(error.response?.data || error);
    }
  } else if (state === "failed") {
    globalErrorHandler(
      syncMetadataTask.error.value ??
        new Error(syncMetadataTask.errorMessage.value ?? "ESO sync failed")
    );
  }
});

// Both outcomes, a failed start included, are reported by the syncMetadataTask state watcher.
const syncMeterMetadataReq = async () => {
  syncMetadataTask.reset();
  await syncMetadataTask.execute(async () => {
    const response = await goSolidApi.post<AsyncTaskStartResponse>(
      "/api/admin/lt-eso/sync-meter-metadata"
    );
    return response.data;
  });
};

// Initial data load
makeApiRequest();
</script>

<style scoped lang="scss">
.table-responsive {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
}

.badge {
  font-size: 0.85em;
}

/* Desktop styles */
@media (min-width: 992px) {
  .table {
    width: 100%;
    min-width: auto;
  }

  .table th,
  .table td {
    white-space: nowrap;
    padding: 1rem;
  }
}

/* Mobile styles */
@media (max-width: 991px) {
  .table {
    min-width: 800px;
  }

  .table th,
  .table td {
    font-size: 0.85rem;
    padding: 0.5rem;
  }
}

.card {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
}

.card-header {
  background-color: transparent;
  border-bottom: 1px solid var(--phoenix-gray-200);
  padding: 1rem;
}

.table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}
</style>
